import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { useCommon } from 'src/domains/common/Common';
import { PriceHistory } from 'src/domains/sportsbook/webview/components/priceHistory/PriceHistory';
import { SelectionId } from 'src_common/common/websocket2/id/WebsocketId';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { RaceSummaryRow } from 'src/domains/sportsbook/webview/components/raceSummary/raceSummarySport/RaceSummaryNew.style';
import {
    SpInProgressNew,
    WinInProgressNew,
} from 'src/domains/sportsbook/webview/components/selectionRaceCard/SelectionRaceCard.style';
import { RaceSummaryRowHorseRacingState } from 'src/domains/sportsbook/webview/components/raceSummary/raceSummaryRowSport/horseRacing/RaceSummaryRowHorseRacing.state';
import { RaceCardRunner } from 'src/domains/sportsbook/webview/components/raceSummary/raceSummaryRowSport/RaceCard/RaceCardRunner';
import { AntePostAndSpecials } from 'src/domains/sportsbook/webview/components/raceSummary/raceSummaryRowSport/AntePostAndSpecials/AntePostAndSepcials';
import { RowDetails } from 'src/domains/sportsbook/webview/components/raceSummary/raceSummaryRowSport/RaceDetails/RaceDetails';
import {
    PriceHistoryCell,
    UnnamedFavouriteLabel,
} from 'src/domains/sportsbook/webview/components/raceSummary/raceSummaryRowSport/horseRacing/RaceSummaryRowHorseRacing.style';

interface PropsType {
    antePost: boolean;
    className?: string;
    dataTest?: string;
    isPriceBoostActive?: boolean;
    isNonRunner?: boolean;
    isPriceHistoryHidden?: boolean;
    isRaceInProgress: boolean;
    isSpecials?: boolean;
    isUnnamedFavourite: boolean;
    runner?: React.ReactNode;
    selectionId: SelectionId;
    sp?: React.ReactNode;
    tagsCountry?: string | undefined;
    winner?: React.ReactNode;
}

export const RaceSummaryRowHorseRacing = observer('RaceSummaryRowHorseRacing', (props: PropsType) => {
    const common = useCommon();
    const configState = ConfigComponents.get(common).config;
    const [runnerDetailsState] = useState(() => new RaceSummaryRowHorseRacingState());

    const {
        antePost,
        className,
        dataTest,
        isPriceBoostActive,
        isNonRunner,
        isPriceHistoryHidden,
        isRaceInProgress,
        isSpecials,
        isUnnamedFavourite,
        runner,
        selectionId,
        sp,
        tagsCountry,
        winner,
    } = props;

    const selectionModel = selectionId.getModel();
    if (selectionModel === null) {
        return null;
    }

    const marketModel = selectionModel.getMarket();
    if (marketModel === null) {
        return null;
    }

    const isOnlyPrice = marketModel.newTemplateId === 'win-only';

    const isRasGoldActive = tagsCountry !== undefined && configState.rasGoldCountries.includes(tagsCountry);
    const isRaceRowExpanded = isRasGoldActive && runnerDetailsState.expandedRaceId === selectionId.key;

    if (isUnnamedFavourite) {
        return (
            <RaceSummaryRow
                className={className}
                isNonRunner={isNonRunner}
                data-test={dataTest}
                variant={configState.componentVariants.sportsbookVariant}
                isExpanded={isRaceRowExpanded}
            >
                <UnnamedFavouriteLabel isAntePost={antePost}>{runner}</UnnamedFavouriteLabel>
                {isRaceInProgress === true ? <SpInProgressNew isPriceHistoryHidden={isPriceHistoryHidden} /> : sp}
            </RaceSummaryRow>
        );
    } else if (antePost) {
        return (
            <AntePostAndSpecials
                selectionId={selectionId}
                winner={winner}
            />
        );
    } else if (isSpecials === true) {
        return (
            <AntePostAndSpecials
                selectionId={selectionId}
                winner={winner}
            />
        );
    } else {
        const handleDetailsClick = (): void => {
            runnerDetailsState.handleDetailsClick(selectionModel.id2.key);
        };

        const renderWinSection = isRaceInProgress ? (
            <WinInProgressNew isPriceHistoryHidden={isPriceHistoryHidden} />
        ) : (
            winner
        );

        const renderSpSection = isRaceInProgress ? <SpInProgressNew isPriceHistoryHidden={isPriceHistoryHidden} /> : sp;

        const renderPriceHistory =
            isPriceHistoryHidden === true ? null : (
                <PriceHistoryCell
                    isRaceInProgress={isRaceInProgress}
                    variant={configState.componentVariants.sportsbookVariant}
                >
                    <PriceHistory selectionId={selectionId} />
                </PriceHistoryCell>
            );

        const renderRowDetails = isRaceRowExpanded ? (
            <RowDetails
                selectionId={selectionId}
                isNonRunner={isNonRunner}
            />
        ) : null;

        return (
            <li>
                <ul>
                    <RaceSummaryRow
                        className={className}
                        isNonRunner={isNonRunner}
                        data-test={dataTest}
                        variant={configState.componentVariants.sportsbookVariant}
                        isExpanded={isRaceRowExpanded}
                    >
                        <RaceCardRunner
                            selectionId={selectionId}
                            handleDetailsClick={handleDetailsClick}
                            isExpanded={isRaceRowExpanded}
                            isPriceBoostActive={isPriceBoostActive}
                            tagsCountry={tagsCountry}
                        />

                        {isOnlyPrice === true ? (
                            renderWinSection
                        ) : (
                            <>
                                {renderPriceHistory}
                                {renderWinSection}
                                {renderSpSection}
                            </>
                        )}
                    </RaceSummaryRow>

                    {renderRowDetails}
                </ul>
            </li>
        );
    }
});
